import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Users, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function VeggieStirFryRecipe() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Recipe Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
            Week 2 • Day 5 Recipe
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Colorful Veggie Stir-Fry
          </h1>
          <p className="text-lg text-secondary-600">
            A vibrant, nutritious dish featuring 5 colorful vegetables
          </p>
        </div>

        {/* Recipe Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_radiance40-1/artifacts/ewr0gfxt_5%20veggie%20bowl.png"
            alt="Colorful Veggie Stir-Fry"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Recipe Info */}
        <div className="flex flex-wrap justify-center gap-6 mb-8">
          <div className="flex items-center gap-2 text-secondary-700">
            <Clock className="w-5 h-5 text-primary" />
            <span>25 minutes</span>
          </div>
          <div className="flex items-center gap-2 text-secondary-700">
            <Users className="w-5 h-5 text-primary" />
            <span>Serves 2-3</span>
          </div>
          <div className="flex items-center gap-2 text-secondary-700">
            <ChefHat className="w-5 h-5 text-primary" />
            <span>Easy</span>
          </div>
        </div>

        {/* Ingredients */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-3">
            <span className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">1</span>
            Ingredients
          </h2>
          <ul className="grid md:grid-cols-2 gap-3">
            {[
              "1 cup yellow bell pepper, sliced thinly",
              "1 cup purple cabbage, finely shredded",
              "1 cup carrots, sliced into thin rounds",
              "1 cup broccoli florets",
              "1 cup red bell pepper, sliced thinly",
              "2 tablespoons olive oil",
              "2 cloves garlic, finely minced",
              "1 tablespoon fresh ginger, finely minced",
              "2 tablespoons low-sodium soy sauce",
              "1 tablespoon sesame seeds (optional)",
              "Salt and pepper to taste"
            ].map((ingredient, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-secondary-700">{ingredient}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Instructions */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-3">
            <span className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">2</span>
            Instructions
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-secondary-900 mb-3 text-lg">Preparation</h3>
              <ul className="space-y-2 text-secondary-700">
                <li>• Wash all the vegetables thoroughly.</li>
                <li>• Slice both the red and yellow bell peppers into thin strips.</li>
                <li>• Shred the purple cabbage finely.</li>
                <li>• Slice the carrots into thin rounds.</li>
                <li>• Cut broccoli into small florets.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-secondary-900 mb-3 text-lg">Steaming Vegetables</h3>
              <ul className="space-y-2 text-secondary-700">
                <li>• Fill a large pot with about 1 inch of water and bring it to a boil.</li>
                <li>• Place broccoli, red, and yellow bell peppers, cabbage, and carrot slices into a steamer basket.</li>
                <li>• Cover and steam for about 5-7 minutes until the vegetables are tender yet vibrant.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-secondary-900 mb-3 text-lg">Stir-Fry</h3>
              <ul className="space-y-2 text-secondary-700">
                <li>• Heat olive oil in a large skillet or wok over medium heat.</li>
                <li>• Add the minced garlic and ginger, and sauté for about 1 minute until fragrant.</li>
                <li>• Add the steamed vegetables to the skillet.</li>
                <li>• Pour the soy sauce over the vegetables, stirring well to coat them evenly.</li>
                <li>• Stir-fry for an additional 3-4 minutes to allow the flavors to meld.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-secondary-900 mb-3 text-lg">Final Touch</h3>
              <ul className="space-y-2 text-secondary-700">
                <li>• Season with salt and pepper to taste.</li>
                <li>• Optionally, sprinkle sesame seeds over the top for added texture and a nutty flavor.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-secondary-900 mb-3 text-lg">Serving</h3>
              <p className="text-secondary-700">
                Serve the Colorful Veggie Stir-Fry hot. It can be enjoyed on its own or as a vibrant side dish to complement your favorite protein.
              </p>
            </div>
          </div>
        </section>

        {/* Nutritional Benefits */}
        <section className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 shadow-md border-2 border-green-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-3">
            <span className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">✓</span>
            Nutritional Benefits
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { veggie: "Broccoli", benefit: "Rich source of vitamins K and C, fiber, and antioxidants." },
              { veggie: "Red Bell Pepper", benefit: "High in vitamin C and a good source of vitamin A." },
              { veggie: "Yellow Bell Pepper", benefit: "Provides vitamin C, folate, and potassium." },
              { veggie: "Purple Cabbage", benefit: "Packed with antioxidants, vitamin C, and fiber." },
              { veggie: "Carrots", benefit: "Excellent source of beta-carotene, fiber, vitamin K1, and potassium." }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="font-bold text-green-700 mb-1">{item.veggie}</h4>
                <p className="text-sm text-secondary-600">{item.benefit}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-center text-secondary-700 font-medium">
            This dish not only enhances your meal with a burst of colors and flavors but also supports a healthy diet. Enjoy the delightful textures and nutritious benefits!
          </p>
        </section>
      </main>
    </div>
  );
}

export default VeggieStirFryRecipe;
